import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, IconButton, InputAdornment, TextField, Tooltip} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityAddressLookup from '../../../shared-components/AdmicityAddressLookup';
import {HOVER_BOX} from '../../../shared-components/InfoHover';

const SCHOOL_ADDRESS_HOVER = "The school's address is used to calculate the straight-line distance in miles between the school and each applicant's home address. The distance is shown on the applicant's profile and applicants can be sorted by it within each admissions category.";

export const formatSchoolAddress = address => [
    address?.apartment,
    address?.houseName,
    address?.houseNumber && address?.street ? `${address.houseNumber} ${address.street}` : address?.houseNumber || address?.street,
    address?.district,
    address?.town,
    address?.county,
    address?.postcode
].filter(part => typeof part === 'string' && part.trim()).join(', ');

/**
 * AD-2228: the school's address while School Setup is open. The school's details can arrive after the page has
 * opened, so the address is read again whenever they do, as the other settings on the page are; otherwise the box
 * stayed empty although the address was saved.
 */
export const useSchoolAddress = schoolAddress => {
    const [address, setAddress] = useState(schoolAddress ?? null);

    useEffect(() => {
        setAddress(schoolAddress ?? null);
    }, [schoolAddress]);

    return [address, setAddress];
};

// AD-2228: one box the height of the others, under School Name, so the grid stays in line; the address search opens
// in a pop-up from the pencil
const SchoolAddressField = ({value, onChange}) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);

    const openSearch = () => {
        setDraft(value);
        setOpen(true);
    };

    return (
        <>
            <TextField
                label="School Address"
                size="small"
                fullWidth
                margin="dense"
                value={formatSchoolAddress(value)}
                onClick={openSearch}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end" sx={{gap: 0.5}}>
                            <Tooltip title={SCHOOL_ADDRESS_HOVER} arrow placement="top" componentsProps={HOVER_BOX}>
                                <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{color: '#0A8572', cursor: 'help'}}
                                    data-testid="hover-schoolAddress"
                                />
                            </Tooltip>
                            <IconButton size="small" aria-label="Change the school address" onClick={openSearch}>
                                <EditOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                inputProps={{'data-testid': 'school-address', style: {cursor: 'pointer'}}}
            />
            <AdmicityDialog
                open={open}
                handleClose={() => setOpen(false)}
                title="School Address"
                maxWidth="md"
                actions={[
                    {label: 'Cancel', onClick: () => setOpen(false)},
                    {
                        label: 'OK',
                        disabled: !draft?.postcode,
                        onClick: () => {
                            onChange(draft);
                            setOpen(false);
                        }
                    }
                ]}
            >
                {/* room above the Country box, so its label is not cut off by the top of the pop-up */}
                <Box sx={{pt: 1}}>
                    <AdmicityAddressLookup
                        withReferences
                        initialValue={value ? {...value, country: value.country || 'GBR'} : undefined}
                        defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
                        onAddressSelect={setDraft}
                        enableValidation={false}
                    />
                </Box>
            </AdmicityDialog>
        </>
    );
};

SchoolAddressField.propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired
};

export default SchoolAddressField;
